import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { Storage, deleteObject, getDownloadURL, ref, uploadBytesResumable } from '@angular/fire/storage';
import { NGXLogger } from 'ngx-logger';
import { Observable, catchError, map, of } from 'rxjs';
import { OrganizationRequest, OrganizationRequestInput } from '../models/organization-request.model';

/**
 * Requests to create an organization. Users submit them with supporting documents; platform admins
 * (Firebase custom claim `platformAdmin`) approve or reject them. Approval creates the organization
 * on the server with the requester as its admin.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationRequestService {
  private afs = inject(AngularFirestore);
  private storage = inject(Storage);
  private auth = inject(Auth);
  private logger = inject(NGXLogger);

  static readonly MAX_DOCUMENTS = 5;
  static readonly MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
  static readonly DOCUMENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

  /** New id for a request; documents are uploaded under it before submitting */
  newRequestId(): string {
    return this.afs.createId();
  }

  async uploadDocument(uid: string, requestId: string, file: File): Promise<string> {
    const safeName = file.name.replace(/[^A-Za-z0-9._-]+/g, '_').slice(-120) || 'document';
    const path = `organization_requests/${uid}/${requestId}/${Date.now()}_${safeName}`;
    this.logger.debug('organization-request.service.ts :: Uploading request document:', {
      requestId,
      path,
      size: file.size,
      type: file.type,
    });
    await uploadBytesResumable(ref(this.storage, path), file, { contentType: file.type });
    this.logger.debug('organization-request.service.ts :: Request document uploaded:', { path });
    return path;
  }

  async deleteDocument(path: string): Promise<void> {
    this.logger.debug('organization-request.service.ts :: Deleting request document:', { path });
    await deleteObject(ref(this.storage, path));
  }

  async documentUrl(path: string): Promise<string> {
    return getDownloadURL(ref(this.storage, path));
  }

  async submitRequest(input: OrganizationRequestInput): Promise<string> {
    const result = await this.callFunction<{ requestId: string }>('orgRequests_submit', { ...input });
    return result.requestId;
  }

  async reviewRequest(requestId: string, approve: boolean, note: string): Promise<{ status: string; organizationId: string }> {
    return this.callFunction('orgRequests_review', { requestId, approve, note });
  }

  /** The signed-in user's own requests, newest first */
  getMyRequests(uid: string): Observable<OrganizationRequest[]> {
    if (!uid) return of([]);
    return this.afs
      .collection<OrganizationRequest>('organization_requests', (q) => q.where('requesterId', '==', uid))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((list) => [...list].sort((a, b) => (b.created?.toMillis?.() ?? 0) - (a.created?.toMillis?.() ?? 0))),
        catchError((err) => {
          this.logger.error('organization-request.service.ts :: Error loading my requests:', { uid, err });
          return of([]);
        })
      );
  }

  /** Recent requests for platform admins to review, newest first */
  getRequestsForReview(limit = 100): Observable<OrganizationRequest[]> {
    return this.afs
      .collection<OrganizationRequest>('organization_requests', (q) => q.orderBy('created', 'desc').limit(limit))
      .valueChanges({ idField: 'id' })
      .pipe(
        catchError((err) => {
          this.logger.error('organization-request.service.ts :: Error loading requests for review:', { err });
          return of([]);
        })
      );
  }

  /**
   * True when the signed-in user has the platformAdmin custom claim. Refreshes the token so a newly
   * granted claim applies without signing out (one token refresh per call).
   */
  async isPlatformAdmin(): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) return false;
    try {
      const token = await user.getIdTokenResult(true);
      return token.claims['platformAdmin'] === true;
    } catch (err) {
      this.logger.error('organization-request.service.ts :: Reading platform admin claim failed:', { err });
      return false;
    }
  }

  private async callFunction<T>(name: string, data: Record<string, any>): Promise<T> {
    this.logger.debug(`organization-request.service.ts :: Calling ${name} Cloud Function with params:`, data);
    try {
      const result = await httpsCallable<Record<string, any>, T>(getFunctions(), name)(data);
      this.logger.debug(`organization-request.service.ts :: ${name} returned:`, result.data);
      return result.data;
    } catch (err: any) {
      this.logger.error(`organization-request.service.ts :: Error calling ${name}: [${err?.code}] ${err?.message}`, {
        params: data,
      });
      throw new Error(err?.message || 'Something went wrong. Please try again.');
    }
  }
}
